import { useEffect, useState } from 'react';
import { FieldProps } from 'formik';
import { Input, Text } from '@chakra-ui/react';
import { parseTargetInput, fmtTargetDisplay } from '../vto.util';

interface NumericTargetInputProps extends FieldProps {
  isEditing: boolean;
  placeholder?: string;
  notDefinedText?: string;
}

/**
 * Formik-aware numeric target input for V/TO financial goals.
 * - Edit mode: accepts shorthand ($50M, 50M, 500K, 50000000, $13,000,000); on blur it
 *   parses to a raw number (stored in Formik) and reformats the display to shorthand.
 * - Read-only mode: displays "$" + shorthand, or the notDefinedText when unset/zero.
 */
export function NumericTargetInput({
  field,
  form,
  isEditing,
  placeholder = 'e.g., 50M',
  notDefinedText = 'Not set',
}: NumericTargetInputProps) {
  const numericValue: number =
    typeof field.value === 'number' ? field.value : parseTargetInput(field.value);
  const [display, setDisplay] = useState<string>(fmtTargetDisplay(numericValue));

  // Keep the shorthand display in sync when the underlying value changes externally
  // (e.g., a different V/TO document loads via Formik enableReinitialize).
  useEffect(() => {
    setDisplay(fmtTargetDisplay(numericValue));
  }, [numericValue]);

  if (!isEditing) {
    if (!numericValue) {
      return (
        <Text as="span" fontSize="sm" color="gray.400" fontStyle="italic">
          {notDefinedText}
        </Text>
      );
    }
    return (
      <Text as="span" fontSize="sm" color="gray.700">
        ${fmtTargetDisplay(numericValue)}
      </Text>
    );
  }

  return (
    <Input
      value={display}
      onChange={(e) => setDisplay(e.target.value)}
      onBlur={() => {
        const parsed = parseTargetInput(display);
        form.setFieldValue(field.name, parsed);
        setDisplay(fmtTargetDisplay(parsed));
      }}
      placeholder={placeholder}
      size="sm"
    />
  );
}
